import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  BarChart3,
  PieChart,
  MessageSquare,
  Send,
  Loader2,
  ChevronLeft,
  Lightbulb,
  Target,
  Shield,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Building2,
  Calendar,
  Info,
  RefreshCw,
  Download,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rating badge component
const RatingBadge = ({ rating, confidence }) => {
  const colors = {
    BUY: "bg-gradient-to-r from-green-500 to-emerald-500",
    HOLD: "bg-gradient-to-r from-amber-500 to-yellow-500",
    SELL: "bg-gradient-to-r from-red-500 to-rose-500",
  };

  const icons = {
    BUY: <TrendingUp className="w-5 h-5" />,
    HOLD: <Minus className="w-5 h-5" />,
    SELL: <TrendingDown className="w-5 h-5" />,
  };

  return (
    <div className={`${colors[rating]} px-4 py-2 rounded-xl text-white flex items-center gap-2 shadow-lg`}>
      {icons[rating]}
      <span className="font-bold text-lg">{rating}</span>
      {confidence && (
        <span className="text-sm opacity-90">({confidence}% confidence)</span>
      )}
    </div>
  );
};

// Metric card component
const MetricCard = ({ label, value, change, unit, onClick }) => {
  const isPositive = change > 0;
  const isNegative = change < 0;

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl p-4 text-left hover:shadow-md transition-shadow border border-slate-100 w-full"
    >
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-slate-800">{value}</p>
      <div className="flex items-center gap-1 mt-1">
        {change !== undefined && change !== null && (
          <>
            {isPositive && <ArrowUpRight className="w-3 h-3 text-green-500" />}
            {isNegative && <ArrowDownRight className="w-3 h-3 text-red-500" />}
            {!isPositive && !isNegative && <Minus className="w-3 h-3 text-slate-400" />}
            <span className={`text-xs ${isPositive ? "text-green-600" : isNegative ? "text-red-600" : "text-slate-500"}`}>
              {isPositive ? "+" : ""}{change}% {unit || "YoY"}
            </span>
          </>
        )}
      </div>
    </button>
  );
};

// Red flag card component
const RedFlagCard = ({ flag }) => {
  const severityColors = {
    high: "border-red-400 bg-red-50",
    medium: "border-amber-400 bg-amber-50",
    low: "border-blue-400 bg-blue-50",
  };

  const severityIcons = {
    high: <XCircle className="w-5 h-5 text-red-500" />,
    medium: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    low: <Info className="w-5 h-5 text-blue-500" />,
  };

  return (
    <div className={`rounded-xl p-4 border-l-4 ${severityColors[flag.severity]}`}>
      <div className="flex items-start gap-3">
        {severityIcons[flag.severity]}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-slate-800">{flag.title}</p>
            <Badge variant="outline" className="text-xs capitalize">{flag.severity}</Badge>
          </div>
          <p className="text-sm text-slate-600">{flag.description}</p>
          {flag.impact && (
            <p className="text-xs text-slate-500 mt-2">
              <span className="font-medium">Potential Impact:</span> {flag.impact}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Insight card component
const InsightCard = ({ insight }) => {
  const typeStyles = {
    positive: { bg: "bg-green-50", border: "border-green-200", icon: <CheckCircle className="w-5 h-5 text-green-500" /> },
    negative: { bg: "bg-red-50", border: "border-red-200", icon: <XCircle className="w-5 h-5 text-red-500" /> },
    neutral: { bg: "bg-slate-50", border: "border-slate-200", icon: <Info className="w-5 h-5 text-slate-500" /> },
  };

  const style = typeStyles[insight.type] || typeStyles.neutral;

  return (
    <div className={`rounded-xl p-4 border ${style.bg} ${style.border}`}>
      <div className="flex items-start gap-3">
        {style.icon}
        <div>
          <p className="font-semibold text-slate-800 mb-1">{insight.title}</p>
          <p className="text-sm text-slate-600">{insight.description}</p>
          {insight.implication && (
            <p className="text-xs text-slate-500 mt-2 italic">{insight.implication}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Risk score component
const RiskScore = ({ label, score, status }) => {
  const getColor = (score) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="font-medium text-slate-700">{label}</p>
        <Badge variant="outline" className="text-xs">{status}</Badge>
      </div>
      <div className="flex items-center gap-3">
        <Progress value={score} className="flex-1 h-2" />
        <span className="text-sm font-bold text-slate-700">{score}/100</span>
      </div>
    </div>
  );
};

const FinancialDocumentAnalyzer = ({ onBack }) => {
  const [stage, setStage] = useState("upload"); // upload, loading, analysis
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  // Loading messages
  const loadingMessages = [
    "Reading document structure...",
    "Extracting financial data...",
    "Analyzing key metrics...",
    "Detecting red flags...",
    "Generating AI insights...",
    "Preparing recommendations...",
  ];

  useEffect(() => {
    if (stage === "loading") {
      let messageIndex = 0;
      let progress = 0;
      
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[messageIndex]);
      }, 2500);

      const progressInterval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress > 90) progress = 90;
        setLoadingProgress(progress);
      }, 1000);

      return () => {
        clearInterval(messageInterval);
        clearInterval(progressInterval);
      };
    }
  }, [stage]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = [".pdf", ".txt", ".doc", ".docx"];
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf("."));
    
    if (!validTypes.includes(ext)) {
      toast.error("Please upload a PDF, TXT, DOC, or DOCX file");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }

    setFile(selectedFile);
    analyzeDocument(selectedFile);
  };

  const analyzeDocument = async (selectedFile) => {
    setStage("loading");
    setLoadingMessage(loadingMessages[0]);
    setLoadingProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API_URL}/api/documents/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 minutes timeout for large documents
      });

      setAnalysis(response.data.analysis);
      setSessionId(response.data.session_id);
      setLoadingProgress(100);
      
      // Add smart questions as initial chat suggestions
      if (response.data.analysis?.smart_questions) {
        setChatMessages([{
          role: "assistant",
          content: "I've analyzed the document. Here are some questions you might want to ask:",
          suggestions: response.data.analysis.smart_questions.slice(0, 5),
        }]);
      }

      setTimeout(() => setStage("analysis"), 500);
      toast.success("Document analyzed successfully!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze document. Please try again.");
      setStage("upload");
      setFile(null);
    }
  };

  const handleChatSubmit = async (message = chatInput) => {
    if (!message.trim() || !sessionId) return;

    const userMessage = { role: "user", content: message };
    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    setChatLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/documents/chat`, {
        session_id: sessionId,
        message: message,
        document_context: analysis?.executive_summary || "",
      });

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I couldn't process that question. Please try again." },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const resetAnalysis = () => {
    setStage("upload");
    setFile(null);
    setAnalysis(null);
    setChatMessages([]);
    setSessionId(null);
    setActiveTab("overview");
  };

  return (
    <div className="space-y-6" data-testid="financial-document-analyzer">
      {/* Back button */}
      {onBack && (
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-slate-600 hover:text-navy-700"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </Button>
      )}

      {/* Header */}
      <div className="text-center">
        <Badge className="bg-blue-100 text-blue-700 mb-3">AI Analysis Tool</Badge>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
          Financial Document AI Analyzer
        </h2>
        <p className="text-slate-600 text-sm max-w-xl mx-auto">
          Upload 10-K, 10-Q, earnings reports, or investor presentations. Get instant AI analysis with buy/hold/sell recommendations.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {/* Upload Stage */}
        {stage === "upload" && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Upload Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative cursor-pointer rounded-2xl border-2 border-dashed p-12
                transition-all duration-300
                ${dragActive 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-slate-300 bg-gradient-to-br from-slate-50 to-blue-50 hover:border-blue-400 hover:bg-blue-50"
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.doc,.docx"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
              />
              
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-lg font-semibold text-slate-700 mb-2">
                  Drop your document here
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  or <span className="text-blue-600 font-medium">click to browse</span>
                </p>
                <p className="text-xs text-slate-400">
                  Supports: PDF, TXT, DOC, DOCX (max 10MB)
                </p>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="pt-6">
                  <Zap className="w-8 h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-1">Instant Analysis</h3>
                  <p className="text-sm text-slate-600">
                    Extract key metrics, ratios, and financial data in seconds
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-50">
                <CardContent className="pt-6">
                  <AlertTriangle className="w-8 h-8 text-red-600 mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-1">Red Flag Detection</h3>
                  <p className="text-sm text-slate-600">
                    AI identifies risks, accounting issues, and warning signs
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="pt-6">
                  <MessageSquare className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-1">Ask Anything</h3>
                  <p className="text-sm text-slate-600">
                    Chat with AI about any aspect of the document
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Not Financial Advice:</strong> This tool provides educational analysis only. 
                  Always consult a licensed financial advisor before making investment decisions. 
                  AI analysis may contain errors or misinterpretations.
                </span>
              </p>
            </div>
          </motion.div>
        )}

        {/* Loading Stage */}
        {stage === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-12"
          >
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-50" />
              <div className="relative w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <FileText className="w-12 h-12 text-white" />
              </div>
            </div>
            
            <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
              Analyzing Document
            </h3>
            <p className="text-slate-600 mb-6">{loadingMessage}</p>
            
            <div className="max-w-sm mx-auto">
              <Progress value={loadingProgress} className="h-2 mb-2" />
              <p className="text-xs text-slate-400">This may take up to 60 seconds for complex documents</p>
            </div>

            {file && (
              <div className="mt-6 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">{file.name}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Analysis Stage */}
        {stage === "analysis" && analysis && (
          <motion.div
            key="analysis"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Header with Company Info and Rating */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div>
                <Button variant="ghost" size="sm" onClick={resetAnalysis} className="mb-2 -ml-2">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  New Analysis
                </Button>
                <h3 className="text-2xl font-bold text-slate-800">
                  {analysis.company_info?.name || "Document Analysis"}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                  {analysis.company_info?.document_type && (
                    <Badge variant="outline">{analysis.company_info.document_type}</Badge>
                  )}
                  {analysis.company_info?.period && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {analysis.company_info.period}
                    </span>
                  )}
                </div>
              </div>
              {analysis.recommendation && (
                <RatingBadge 
                  rating={analysis.recommendation.rating} 
                  confidence={analysis.recommendation.confidence}
                />
              )}
            </div>

            {/* Executive Summary */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{analysis.executive_summary}</p>
              </CardContent>
            </Card>

            {/* Recommendation Reasoning */}
            {analysis.recommendation && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-0 shadow-sm bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base text-green-800">
                      <CheckCircle className="w-4 h-4" />
                      Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {analysis.recommendation.strengths?.map((item, idx) => (
                        <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                          <span className="text-green-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-red-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base text-red-800">
                      <AlertTriangle className="w-4 h-4" />
                      Concerns
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {analysis.recommendation.concerns?.map((item, idx) => (
                        <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                          <span className="text-red-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Tabs for Detailed Analysis */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full flex-wrap h-auto gap-1 bg-slate-100 p-1 rounded-xl">
                <TabsTrigger value="overview" className="flex-1 min-w-[100px]">
                  <BarChart3 className="w-4 h-4 mr-1" />
                  Metrics
                </TabsTrigger>
                <TabsTrigger value="redflags" className="flex-1 min-w-[100px]">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Red Flags
                </TabsTrigger>
                <TabsTrigger value="insights" className="flex-1 min-w-[100px]">
                  <Lightbulb className="w-4 h-4 mr-1" />
                  Insights
                </TabsTrigger>
                <TabsTrigger value="risk" className="flex-1 min-w-[100px]">
                  <Shield className="w-4 h-4 mr-1" />
                  Risk
                </TabsTrigger>
                <TabsTrigger value="chat" className="flex-1 min-w-[100px]">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Ask AI
                </TabsTrigger>
              </TabsList>

              {/* Key Metrics Tab */}
              <TabsContent value="overview" className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {analysis.key_metrics && Object.entries(analysis.key_metrics).map(([key, metric]) => (
                    <MetricCard
                      key={key}
                      label={metric.label || key.replace(/_/g, " ")}
                      value={metric.value}
                      change={metric.change}
                      unit={metric.unit}
                    />
                  ))}
                </div>
              </TabsContent>

              {/* Red Flags Tab */}
              <TabsContent value="redflags" className="mt-4 space-y-3">
                {analysis.red_flags?.length > 0 ? (
                  analysis.red_flags.map((flag, idx) => (
                    <RedFlagCard key={idx} flag={flag} />
                  ))
                ) : (
                  <div className="text-center py-8 bg-green-50 rounded-xl">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-green-700 font-medium">No major red flags detected</p>
                    <p className="text-sm text-green-600">The document appears relatively clean</p>
                  </div>
                )}
              </TabsContent>

              {/* Insights Tab */}
              <TabsContent value="insights" className="mt-4 space-y-3">
                {analysis.insights?.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </TabsContent>

              {/* Risk Assessment Tab */}
              <TabsContent value="risk" className="mt-4 space-y-4">
                {analysis.risk_assessment && (
                  <>
                    <RiskScore 
                      label="Liquidity Health" 
                      score={analysis.risk_assessment.liquidity_score || 50}
                      status={analysis.risk_assessment.liquidity_status || "Moderate"}
                    />
                    <RiskScore 
                      label="Debt Sustainability" 
                      score={analysis.risk_assessment.debt_score || 50}
                      status={analysis.risk_assessment.debt_status || "Moderate"}
                    />
                    <RiskScore 
                      label="Profitability Strength" 
                      score={analysis.risk_assessment.profitability_score || 50}
                      status={analysis.risk_assessment.profitability_status || "Moderate"}
                    />
                    {analysis.risk_assessment.summary && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <p className="text-sm text-slate-700">{analysis.risk_assessment.summary}</p>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* AI Chat Tab */}
              <TabsContent value="chat" className="mt-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    {/* Chat Messages */}
                    <div className="h-[400px] overflow-y-auto mb-4 space-y-4 pr-2">
                      {chatMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                              msg.role === "user"
                                ? "bg-blue-600 text-white"
                                : "bg-slate-100 text-slate-800"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.suggestions && (
                              <div className="mt-3 space-y-2">
                                {msg.suggestions.map((q, qIdx) => (
                                  <button
                                    key={qIdx}
                                    onClick={() => handleChatSubmit(q)}
                                    className="block w-full text-left text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 transition-colors"
                                  >
                                    {q}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-slate-100 rounded-2xl px-4 py-3">
                            <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Chat Input */}
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleChatSubmit()}
                        placeholder="Ask anything about this document..."
                        className="flex-1"
                        disabled={chatLoading}
                      />
                      <Button 
                        onClick={() => handleChatSubmit()}
                        disabled={!chatInput.trim() || chatLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Disclaimer */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Disclaimer:</strong> This analysis is AI-generated for educational purposes only 
                  and should not be considered financial advice. The buy/hold/sell recommendation is based 
                  on automated analysis which may contain errors. Always conduct your own research and 
                  consult a licensed financial professional before making investment decisions.
                </span>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinancialDocumentAnalyzer;
